"use client";

import { type AriaTextFieldOptions, useTextField } from "@react-aria/textfield";
import { useObjectRef } from "@react-aria/utils";
import type { VariantProps } from "class-variance-authority";
import { type ChangeEvent, type FocusEvent, type ReactNode, type Ref, useEffect, useRef } from "react";
import { type NumberFormatValues, NumericFormat, type NumericFormatProps, type SourceInfo } from "react-number-format";

import { cn } from "../../../utils";
import { useTextFieldRootContext } from "../context";
import { inputContainerStyles, inputStyles } from "../style";
import useInputNumberValidation from "./useInputNumberValidation";

export const INCOMPLETE_NUMBER_VALUES = ["", "-", "-0", "0.", "-0.", ".", "-."];

export interface InputNumberBlurTarget extends HTMLInputElement {
  floatValue?: number;
}

export interface InputNumberBlurEvent extends Omit<FocusEvent<HTMLInputElement>, "target"> {
  target: InputNumberBlurTarget;
}

export type TextFieldInputNumberProps = {
  className?: string;
  containerClassName?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  disabled?: boolean;
  min?: string | number;
  max?: string | number;
  ref?: Ref<HTMLInputElement>;
  isLoading?: boolean;
  isWarning?: boolean;
  onBlur?: (event: InputNumberBlurEvent) => void;
} & Omit<NumericFormatProps, "prefix" | "suffix" | "customInput" | "onBlur" | "size"> &
  Omit<AriaTextFieldOptions<"input">, "inputElementType" | "isDisabled" | "onChange" | "prefix" | "suffix" | "value"> &
  VariantProps<typeof inputStyles> &
  VariantProps<typeof inputContainerStyles>;

const InputNumber = ({
  ref: forwardedRef,
  className,
  containerClassName,
  prefix,
  suffix,
  disabled,
  readOnly,
  variant = "outline",
  color = "primary",
  onChange,
  isAllowed,
  isInvalid,
  isWarning,
  min,
  max,
  isLoading,
  ...props
}: TextFieldInputNumberProps) => {
  const ref = useObjectRef(forwardedRef);
  const currentNumberValue = useRef<number | undefined>(undefined);
  const { setTextField } = useTextFieldRootContext<"input">();

  const textField = useTextField(
    {
      ...props,
      value: props.value ? String(props.value) : undefined,
      isReadOnly: readOnly,
      isDisabled: disabled,
      inputElementType: "input",
      // https://github.com/adobe/react-spectrum/issues/1860#issuecomment-849833808
      label: true,
      isInvalid,
    },
    ref
  );

  const { inputProps, isInvalid: isInvalidTextField } = textField;

  useEffect(() => {
    setTextField(textField);
  }, [isInvalidTextField]);

  const handleFocus = () => ref.current?.focus();
  const handleValueChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    currentNumberValue.current = values.floatValue;
    if (props.onValueChange) {
      props.onValueChange(values, sourceInfo);
      return;
    }

    if (sourceInfo.source === "prop") {
      return;
    }

    if (INCOMPLETE_NUMBER_VALUES.includes(values.value)) {
      onChange?.({
        target: {
          value: values.value,
          name: props.name,
        },
      } as ChangeEvent<HTMLInputElement>);
      return;
    }

    if (typeof values.floatValue === "number" && Number.isFinite(values.floatValue)) {
      onChange?.({
        target: {
          value: values.floatValue,
          name: props.name,
        },
      } as unknown as ChangeEvent<HTMLInputElement>);
    }
  };

  useEffect(() => {
    if (props.value === undefined && ref.current) {
      ref.current.value = "";
      currentNumberValue.current = undefined;
    }
  }, [props.value, ref]);

  const validate = useInputNumberValidation({ min, max, isAllowed });

  return (
    <>
      <style>
        {`input::-webkit-outer-spin-button,
          input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }

          input[type=number] {
            -moz-appearance: textfield;
          }`}
      </style>
      <div
        className={cn(inputContainerStyles({ variant, color }), containerClassName)}
        data-invalid={isInvalid}
        data-warning={isWarning}
        data-disabled={inputProps.disabled}
        role="button"
        tabIndex={-1}
        onKeyUp={() => null}
        onClick={handleFocus}
      >
        {prefix}

        <NumericFormat
          {...props}
          {...inputProps}
          defaultValue={props.defaultValue ?? ""}
          value={props.value ?? ""}
          className={cn(inputStyles({ variant }), className)}
          type={["text", "tel", "password"].includes(inputProps.type as string) ? props.type : undefined}
          isAllowed={validate}
          getInputRef={ref}
          onValueChange={handleValueChange}
          onBlur={(event: FocusEvent<HTMLInputElement>) => {
            const target = event.target as InputNumberBlurTarget;
            target.floatValue = currentNumberValue.current;
            inputProps.onBlur?.(event);
          }}
        />

        {suffix}
      </div>
    </>
  );
};

InputNumber.displayName = "InputNumber";

export default InputNumber;
